import { makeAutoObservable, runInAction } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { LINK_HELP_GET_A_BUSINESS_TAX_ID } from 'shared/constants/links'
import { ComplianceApi, type INumberType, type ITargetCountry } from 'entities/Compliance'
import { Summary } from 'entities/Compliance/model'
import { type IQuestionnaireStepsSaveSummary } from 'entities/Compliance/store/questionnaireStepsStore/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class QuestionnaireStepsStore {
  private _targetCountry: ITargetCountry | null = null
  private _isTaxId: boolean | null = null
  private _numberType: INumberType | null = null
  private _loadingUpdate = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get disabledNumberType() {
    return !this._numberType || this._loadingUpdate
  }

  get disabledTargetCountry() {
    return this._targetCountry === null
  }

  get disabledIsTaxId() {
    return this._isTaxId === null
  }

  get targetCountry() {
    return this._targetCountry
  }

  get isTaxId() {
    return this._isTaxId
  }

  get numberType() {
    return this._numberType
  }

  get isNumberTypeLocal() {
    return this.numberType === 'local'
  }

  get isUSTargetCountry() {
    return this.targetCountry === 'US'
  }

  get isCATargetCountry() {
    return this.targetCountry === 'CA'
  }

  get isAllTargetCountry() {
    return this.targetCountry === 'ALL'
  }

  get isAllGBTargetCountry() {
    return this.targetCountry === 'ALL_GB'
  }

  get isGBTargetCountry() {
    return this.targetCountry === 'GB'
  }

  get businessTaxIDLink() {
    return LINK_HELP_GET_A_BUSINESS_TAX_ID
  }

  get isShowTaxIdBanner() {
    return (
      !this.isTaxId &&
      (this.isUSTargetCountry ||
        this.isAllTargetCountry ||
        this.isAllGBTargetCountry ||
        (this.isCATargetCountry && this._numberType !== null && this._numberType !== 'local'))
    )
  }

  setTargetCountry = (country: ITargetCountry | null) => {
    this._targetCountry = country
  }

  setIsTaxId = (isTaxId: boolean | null) => {
    this._isTaxId = isTaxId
  }

  setNumberType = (numberType: INumberType | null) => {
    this._numberType = numberType
  }

  handleSetTargetCountry = (country: ITargetCountry) => {
    this.setTargetCountry(country)

    if (!this.isTaxId) {
      this.setNumberType(null)
    }
  }

  handleSetIsTaxId = (isTaxId: boolean) => {
    this.setIsTaxId(isTaxId)

    if (!isTaxId) {
      this.setNumberType(null)
    }
  }

  saveSummary = async ({
    isRedirect = true,
    isOpenModalSelectNumber = true,
  }: IQuestionnaireStepsSaveSummary = {}) => {
    if (!this._targetCountry || this._isTaxId === null || !this._numberType) return

    try {
      runInAction(() => {
        this._loadingUpdate = true
      })

      const { data } = await ComplianceApi.saveComplianceSummary({
        target_country: this._targetCountry === 'ALL_GB' ? 'ALL' : this._targetCountry,
        have_tax_ein: this._isTaxId,
        numbers_type: this._numberType,
      })

      const summary = new Summary(data)

      this._entities.complianceStore.setSummary(summary)

      if (isRedirect) {
        uiStore.changeRoute({
          path: 'settings/organization/compliance',
        })
      }

      if (isOpenModalSelectNumber) {
        this._entities.numbersStore.initSelectNumberModal()
      }
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loadingUpdate = false
      })
    }
  }
}
